import { useEffect, useMemo, useState } from 'react'
import Highcharts from 'highcharts/highmaps'
import HighchartsReact from 'highcharts-react-official'

import { formatInteger } from './geographicSalesIntelligenceUtils'

const US_MAP_URL =
  'https://code.highcharts.com/mapdata/countries/us/us-all.topo.json'

let usMapPromise = null

function loadUsMapTopology() {
  if (!usMapPromise) {
    usMapPromise = fetch(US_MAP_URL).then((response) => {
      if (!response.ok) {
        throw new Error('Could not load the US map')
      }
      return response.json()
    })
  }
  return usMapPromise
}

export default function GeographicSalesIntelligenceStateMap({ states }) {
  const [topology, setTopology] = useState(null)
  const [loadError, setLoadError] = useState(null)

  useEffect(() => {
    let cancelled = false
    loadUsMapTopology()
      .then((data) => {
        if (!cancelled) setTopology(data)
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err.message || 'Could not load the US map')
      })
    return () => {
      cancelled = true
    }
  }, [])

  const rows = Array.isArray(states) ? states : []
  const options = useMemo(() => {
    if (!topology) return null
    const maxBuyers = Math.max(1, ...rows.map((row) => Number(row.buyers) || 0))
    return {
      chart: {
        map: topology,
        height: 420,
        backgroundColor: 'transparent',
        spacing: [8, 8, 12, 8],
      },
      title: { text: undefined },
      credits: { enabled: false },
      mapNavigation: {
        enabled: true,
        enableButtons: true,
      },
      colorAxis: {
        min: 0,
        max: maxBuyers,
        stops: [
          [0, '#e0f2fe'],
          [0.4, '#38bdf8'],
          [0.75, '#0284c7'],
          [1, '#0c4a6e'],
        ],
      },
      legend: {
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
      },
      tooltip: {
        pointFormat:
          '<b>{point.name}</b><br/>{point.value} buyers<br/>{point.tickets} tickets',
      },
      series: [
        {
          name: 'Buyers',
          data: rows.map((row) => ({
            'hc-key': row.hcKey,
            value: row.buyers,
            tickets: row.tickets,
          })),
          borderColor: '#ffffff',
          borderWidth: 1,
          nullColor: '#f3f4f6',
          states: {
            hover: { color: '#fbbf24' },
          },
        },
      ],
    }
  }, [rows, topology])

  if (loadError) {
    return (
      <p className="text-muted small mb-0">
        {loadError}.{' '}
        {rows.length
          ? `Top state: ${rows[0].name} (${formatInteger(rows[0].buyers)} buyers).`
          : ''}
      </p>
    )
  }

  if (!options) {
    return <p className="text-muted small mb-0">Loading US map…</p>
  }

  if (!rows.length) {
    return (
      <p className="text-muted small mb-0">
        No U.S. state data yet for complete orders with a purchase location.
      </p>
    )
  }

  return (
    <HighchartsReact
      highcharts={Highcharts}
      constructorType="mapChart"
      options={options}
    />
  )
}
